// First-party funnel events (visit -> practice -> login wall -> sign up), so the effect of product
// changes can be measured. No third parties, no cookies: a random id kept in localStorage, which the
// server stores together with the event type. Browsers asking not to be tracked are left alone.

import { STORAGE_KEYS } from "../state/index.ts";

const ENDPOINT = "/api/events";
const VISIT_KEY = "sparring.chess.LastVisitDay";

function optedOut(): boolean {
    return navigator.globalPrivacyControl === true || navigator.doNotTrack === "1" || navigator.webdriver === true;
}

function anonymousId(): string | null {
    try {
        let id = localStorage.getItem(STORAGE_KEYS.anonymousId);
        if (!id) {
            id = crypto.randomUUID();
            localStorage.setItem(STORAGE_KEYS.anonymousId, id);
        }
        return id;
    } catch (e) {
        return null;
    }
}

function deviceClass(): "mobile" | "desktop" {
    return window.matchMedia("(max-width: 768px)").matches ? "mobile" : "desktop";
}

/** The only details the server keeps; anything else is dropped. */
export interface EventProps {
    line?: string | null;
    practice?: string | null;
    referrer?: string | null;
    /** The language of the page, sent with a visit: which version of the front page somebody came in by. */
    lang?: string | null;
}

/** @param type  event type, one of the server's FunnelEventType values (lower case) */
export function event(type: string, props: EventProps = {}, { beacon = false } = {}): void {
    if (optedOut()) return;
    const anonId = anonymousId();
    if (!anonId) return;
    const { line = null, practice = null, referrer = null, lang = null } = props;
    // Which tool the page is, so the panel can tell an event of the study from one of another tool
    const tool = document.body?.dataset?.tool || null;
    const body = JSON.stringify({ anonId, type, line, practice, device: deviceClass(), referrer, tool, lang });
    if (beacon && navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
        return;
    }
    fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true })
        .catch(() => { /* measuring must never break the app */ });
}

/** One visit per day and device, with the referrer's host only and the language of the page it landed on. */
export function visit(): void {
    const today = new Date().toISOString().slice(0, 10);
    try {
        if (localStorage.getItem(VISIT_KEY) === today) return;
        localStorage.setItem(VISIT_KEY, today);
    } catch (e) {
        return;
    }
    let referrer = null;
    try {
        referrer = document.referrer ? new URL(document.referrer).host : null;
    } catch (e) {
        referrer = null;
    }
    event("visit", { referrer, lang: document.documentElement.lang || null });
}

/** Links this device's anonymous id to the account that just logged in (sign up vs returning login is decided server side). */
export function identify(): void {
    event("identify");
}
